/**
 * The estate: the registry, the attention feed, and what it costs.
 *
 * Hoisted out of the old single-page Landing when the Front Door became three
 * pages. It is a module-level object rather than a fetch inside each page for one
 * reason: Home, Status and Estate all need the same registry, and a fetch per page
 * would mean the portal grid flickering and re-ordering every time somebody looked
 * at the estate table and came back. One load, shared, refreshed on request.
 *
 * Each panel is loaded independently and records its own failure. A portal registry
 * that could not be read must never look like a household with no portals — that is
 * the difference between "Fabric is down" and "you own nothing", and the second one
 * is a lie.
 */
import { api, ApiError } from "./api";

export type Health = {
  ok: boolean;
  latencyMs: number;
  checkedAt: string | null;
  consecutiveFailures: number;
  /** Item 15: answering, but over its own budget three sweeps running. */
  slow?: boolean;
  probeOk?: boolean | null;
  probeDetail?: string | null;
};

export type Portal = {
  id: string;
  name: string;
  blurb: string;
  url: string;
  projectId: string;
  minRole: string;
  order: number;
  status: "live" | "building" | "planned";
  health: Health | null;
};

export type Attention = {
  id: string;
  source: string;
  /** `warn` is the v1 vocabulary, still accepted from deployed modules. */
  severity: "info" | "attention" | "urgent" | "warn";
  text: string;
  deepLink: string;
  dueAt: string | null;
  overdue: boolean;
  digestCount: number;
};

/**
 * Which registry ids are the house as it already stands, versus the programme
 * being built. Kept here rather than in a page so Home and Estate group the same
 * way without one drifting from the other.
 */
export const HOUSE = new Set(["ha", "hq", "screening", "codebots"]);

/** Registry id → design-system glyph. */
export const ICON: Record<string, string> = {
  ha: "house",
  hq: "finance",
  screening: "film",
  codebots: "bot",
  vault: "vault",
  homestead: "homestead",
  waypoint: "waypoint",
  hindsight: "hindsight",
  fabric: "fabric",
  frontdoor: "door",
};

/**
 * What each portal is FOR, in one line, shown on the sign-in screen.
 *
 * Local rather than registry-fed, and the reason is structural: this renders
 * *before* anyone signs in, and `GET /portals` is claim-filtered so it needs a
 * token. There is no public registry endpoint yet. `04-portal-screens.md` asks
 * for this to come from the registry, and it should once a `/public/portals`
 * exists — the same shape as `/public/glance`, which already solved exactly this
 * problem for the house figures.
 *
 * Until then it lives beside ICON, which is the same pattern for the same
 * reason: a small id-keyed map the client owns because the registry cannot
 * answer before sign-in. Adding a portal means adding a line here, and
 * `estateBlurbs()` below will not invent one for an id it does not know.
 *
 * `restrictedTo` is shown as a quiet mark rather than hiding the row. Hiding
 * restricted portals makes the estate look smaller than it is, and turns a
 * later "no access" into a surprise instead of something already understood.
 */
const BLURB: Record<string, { blurb: string; restrictedTo?: "owner" | "partner" }> = {
  ha: { blurb: "Energy, water, security and every room in the house" },
  hq: { blurb: "Accounts, budgets, and what the month actually cost", restrictedTo: "partner" },
  vault: { blurb: "Documents, expiry dates, and the in-case folder", restrictedTo: "partner" },
  screening: { blurb: "What to watch tonight, and the listening room" },
  homestead: { blurb: "Everything the property owns and the jobs it needs", restrictedTo: "partner" },
  waypoint: { blurb: "Trips planned and costed before you commit to one", restrictedTo: "partner" },
  hindsight: { blurb: "The house's long memory — what changed, and when", restrictedTo: "owner" },
  fabric: { blurb: "Who can open what, and what it all costs to run", restrictedTo: "owner" },
};

/**
 * The sign-in explainer, in rail order.
 *
 * Codebots is absent by design — it has external users and is not part of the
 * household. Front Door is absent because it is the page you are standing on.
 */
export function estateBlurbs(): Array<{ id: string; name: string; blurb: string; restrictedTo?: string | null }> {
  const NAMES: Record<string, string> = {
    ha: "HA Dashboard",
    hq: "HQ Finance",
    vault: "Vault",
    screening: "Screening Room",
    homestead: "Homestead",
    waypoint: "Waypoint",
    hindsight: "Hindsight",
    fabric: "Fabric",
  };
  return Object.entries(NAMES).map(([id, name]) => ({
    id,
    name,
    blurb: BLURB[id]!.blurb,
    restrictedTo: BLURB[id]!.restrictedTo ?? null,
  }));
}

class Estate {
  portals = $state<Portal[]>([]);
  attention = $state<Attention[]>([]);
  /** Aggregate spend this month. Null means "not readable", never zero. */
  spend = $state<{ amount: number; currency: string } | null>(null);
  loading = $state(true);
  error = $state<string | null>(null);
  loaded = $state(false);

  /**
   * False when `GET /attention` refused this caller.
   *
   * The 403 was already swallowed below, correctly — a display or guest genuinely
   * cannot read the rail and that must not blank the page. What was missing is the
   * *record* of it: F7's week draws what falls due this week, and with the refusal
   * discarded it had no way to tell "nothing is due" from "you are not allowed to
   * know". The first is a claim about the estate and the second is a fact about the
   * account, and only one of them is safe to put on a screen.
   */
  attentionReadable = $state(true);

  /** Live portals, excluding the Front Door — a tile linking to this page is furniture. */
  get visible(): Portal[] {
    return this.portals.filter((p) => p.id !== "frontdoor");
  }
  get house(): Portal[] {
    return this.visible.filter((p) => HOUSE.has(p.id) && p.status === "live");
  }
  get programme(): Portal[] {
    return this.visible.filter((p) => !HOUSE.has(p.id) && p.status === "live");
  }
  get coming(): Portal[] {
    return this.visible.filter((p) => p.status !== "live");
  }
  get liveCount(): number {
    return this.visible.filter((p) => p.health?.ok).length;
  }
  get downCount(): number {
    return this.visible.filter((p) => p.status === "live" && p.health && !p.health.ok).length;
  }
  get slowCount(): number {
    return this.visible.filter((p) => p.health?.ok && p.health.slow).length;
  }

  async load(): Promise<void> {
    this.loading = true;
    // Reset before asking, or a retry that succeeds would still be reported as
    // unreadable for the rest of the session.
    this.attentionReadable = true;
    try {
      const [p, a] = await Promise.all([
        api.get<{ portals: Portal[] }>("/portals"),
        // Display and guest roles cannot read attention. A 403 there is a correct
        // answer, not an error, so it must not blank the page — but it is written
        // down rather than discarded, so nothing downstream mistakes it for "clear".
        api.get<{ items: Attention[] }>("/attention").catch(() => {
          this.attentionReadable = false;
          return { items: [] as Attention[] };
        }),
      ]);
      this.portals = p.portals;
      this.attention = a.items;
      this.error = null;
      this.loaded = true;
    } catch (e) {
      this.error = e instanceof ApiError ? e.message : "Could not reach Fabric";
    } finally {
      this.loading = false;
    }

    // Owner-only, and the route answers with a note before billing export exists.
    // In both cases the figure stays null and renders as a dash — a confident R0
    // is the one answer nobody should believe.
    await api
      .get<{ monthToDate: number | null; currency: string }>("/cost?days=31")
      .then((c) => (this.spend = c.monthToDate == null ? null : { amount: c.monthToDate, currency: c.currency }))
      .catch(() => (this.spend = null));
  }

  /**
   * Dismiss, or defer. The server turns a dismissal of an item with a deadline into
   * a seven-day snooze, because a licence disc does not stop expiring because you
   * swiped it away.
   */
  async dismiss(id: string): Promise<void> {
    this.attention = this.attention.filter((a) => a.id !== id);
    await api.del(`/attention/${id}`).catch(() => this.load());
  }

  async snooze(id: string, days = 7): Promise<void> {
    this.attention = this.attention.filter((a) => a.id !== id);
    await api.post(`/attention/${id}/snooze`, { days }).catch(() => this.load());
  }
}

export const estate = new Estate();

/** `https://watchlist.helloliam.co.za/` → `watchlist.helloliam.co.za`. */
export function host(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    // A malformed registry URL shows as itself rather than vanishing — an empty
    // line under a tile reads as "no address", which is a different fault.
    return url;
  }
}

export function money(v: { amount: number; currency: string }): string {
  return `${v.currency === "USD" ? "$" : v.currency + " "}${v.amount.toFixed(2)}`;
}
